import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type MutableRefObject,
} from "react";
import { Form } from "antd";
import type { AseoNotesDraftHandle } from "./components/AseoNotesSection";
import { getMsg } from "../../utils/antdStatic";
import api from "../../services/api";
import { aseosApi } from "../../services/aseos.api";
import { propiedadesApi } from "../../services/propiedades.api";
import { useAuth } from "../../context/AuthContext";
import { isAdminRole } from "../../utils/roles.utils";
import { configOptionApi } from "../../services/configOption.api";
import { useWebSocket } from "../../context/WebSocketContext";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import {
  Cleaner,
  Aseo,
  AseoRaw,
  Property,
  UpdateUserRequest,
} from "../../interfaces/api.interfaces";
import dayjs from "dayjs";
import { esAseoDeLaAseadora, mapRawAseo } from "../../utils/aseos.utils";
import {
  repartirPartesIgualesUI,
  reescalarProporcionalUI,
} from "./components/RepartoAseadorasField";
import { useSidebar } from "../../hooks/useSidebar";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { ahoraChile } from "../../utils/fechaChile";
import {
  VENTANA_ASEOS_ABIERTOS_DIAS,
  aseoAbiertoEnVentana,
  normalizarVentanaDias,
} from "../../utils/ventanaAseosAbiertos";
import { consultaMismoDia } from "../../utils/aseosMismoDia";
import { clavesCambiadas } from "../../utils/camposCambiados";
import { telefonoParaGuardar } from "../../utils/telefonoE164";
import { marcarCampoTelefono } from "../../utils/telefonoErrorApi";
import { irAlPrimerError } from "../../utils/formularioErrores";
import type { ErrorApi } from "../../services/apiClient";
import { confirmarAseosMismoDia } from "./components/AvisoAseosMismoDia";
export { mapRawAseo };

// Hook for Aseos page
export const useAseosLogic = (
  userFromContext?: any,
  // Ref al editor de las 3 notas en MODO BORRADOR (modal de creación). Tras
  // crear el aseo, `handleAseoSubmit` llama a `commit(nuevoId)` para persistir
  // texto + archivos staged (round 2026-07-06). Opcional: la página /aseadores
  // reusa este hook sin pasarlo.
  notesDraftRef?: MutableRefObject<AseoNotesDraftHandle | null>,
) => {
  // State for aseadoras
  const [aseadoras, setAseadoras] = useState<Cleaner[]>([]);
  const [sectors, setSectors] = useState<{ id: number; sector: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  // Solo el setter se usa para marcar la transacción de pago en curso.
  const [, setProcessingPago] = useState(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingAseadora, setEditingAseadora] = useState<Cleaner | null>(null);
  // Guardando una aseadora: bloquea el segundo clic de «Crear»/«Actualizar». El `ref` corta el doble clic al instante (el estado
  // se actualiza recién en el render siguiente); el estado enciende el spinner del botón del modal.
  const [savingAseadora, setSavingAseadora] = useState<boolean>(false);
  const guardandoAseadoraRef = useRef<boolean>(false);
  const { sidebarOpen, toggleSidebar } = useSidebar();
  // Solo lectura: el setter no se usa actualmente (la simulación admin se mantiene activada por defecto).
  const [simulateAdmin] = useState<boolean>(true);
  const [form] = Form.useForm();

  // State for aseos
  const [aseos, setAseos] = useState<Aseo[]>([]);
  // Ventana de aseos abiertos (días) que el servidor usó para armar la lista: es configurable en
  // /configuracion, así que el frontend la recibe en cada carga en vez de repetir el número.
  const [ventanaDias, setVentanaDias] = useState<number>(VENTANA_ASEOS_ABIERTOS_DIAS);
  const [aseoModalVisible, setAseoModalVisible] = useState<boolean>(false);
  const [editingAseo, setEditingAseo] = useState<Aseo | null>(null);
  // Nonce que se incrementa cada vez que se abre el modal del aseo. Se usa como
  // `key` del editor de notas embebido para forzar su remontaje limpio en cada
  // apertura (el modal no usa destroyOnClose): así el borrador no arrastra
  // texto/archivos de un aseo a otro, y en edición refresca las notas.
  const [aseoModalNonce, setAseoModalNonce] = useState<number>(0);
  // Guard anti-doble-submit del modal del aseo. El estado alimenta
  // `confirmLoading` (deshabilita el botón OK + spinner); el ref evita la
  // condición de carrera de dos clics rápidos antes de que el estado propague
  // (crear un aseo duplicado + re-subir los archivos staged). Round 2026-07-06.
  const [submittingAseo, setSubmittingAseo] = useState<boolean>(false);
  const submittingAseoRef = useRef<boolean>(false);
  const [aseoForm] = Form.useForm();
  const [aseoAssignmentMode, setAseoAssignmentMode] = useState<
    "designado" | "abierto"
  >("designado");

  // State for properties
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState<boolean>(false);
  const [propertyModalVisible, setPropertyModalVisible] =
    useState<boolean>(false);
  const [propertySearchText, setPropertySearchText] = useState<string>("");
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null,
  );

  // Search state
  const [aseadorasSearchText, setAseadorasSearchText] = useState<string>("");
  const [aseosSearchText, setAseosSearchText] = useState<string>("");
  const [loadingAseos, setLoadingAseos] = useState(false);
  // Filter aseadoras by name
  const filteredAseadoras = useMemo(() => {
    if (!aseadorasSearchText) return aseadoras;
    return aseadoras.filter((aseadora) =>
      fuzzyIncludes(aseadora.nombre, aseadorasSearchText),
    );
  }, [aseadoras, aseadorasSearchText]);

  const { user } = useAuth();
  const { socket, isConnected } = useWebSocket();
  const isAdmin = isAdminRole(userFromContext?.rol);

  const availableSectors = [
    "Horcón",
    "Viña del Mar",
    "Reñaca",
    "Cochoa",
    "Concón",
    "Pucón",
    "Puerto Varas",
    "Ensenada",
    "Papudo",
    "Punta Puyai",
    "Valparaíso",
    "Zapallar",
    "Maitencillo",
    "Laguna de Zapallar",
  ];

  const [lavanderias, setLavanderias] = useState<{ id: number; nombre: string; activo: boolean }[]>([]);
  const [availableBanks, setAvailableBanks] = useState<string[]>([]);
  const [accountTypes, setAccountTypes] = useState<string[]>([]);

  useEffect(() => {
    const fetchOptions = async () => {
      const [bancosRes, cuentasRes] = await Promise.all([
        configOptionApi.list("banco"),
        configOptionApi.list("tipocuenta"),
      ]);
      setAvailableBanks(bancosRes?.data?.map((o) => o.valor) ?? []);
      setAccountTypes(cuentasRes?.data?.map((o) => o.valor) ?? []);
    };
    fetchOptions();
  }, []);

  const mapSectorIdToName = useCallback(
    (sectorId: number | string | undefined | null) => {
      if (sectorId == null) return "––";
      const sid = Number(sectorId);
      const found = sectors.find((s) => Number(s.id) === sid);
      if (found) return found.sector;
      if (Number.isInteger(sid) && sid >= 0 && sid < availableSectors.length)
        return availableSectors[sid];
      if (typeof sectorId === "string") return sectorId;
      return "––";
    },
    [sectors, availableSectors],
  );

  // Fetch data on component mount
  // Endpoints como /users/cleaners y /finanzas/empresa/lavanderias son
  // admin-only (responden 403 al aseador) → solo se piden si es admin.
  useEffect(() => {
    if (isAdmin) {
      fetchAseadoras();
      propiedadesApi.getLavanderias().then((r) => setLavanderias(r?.data ?? []));
    } else {
      setLoading(false);
    }
    fetchAseos();
    fetchProperties();
    fetchSectors();
  }, [isAdmin]);

  // Toggle admin mode
  // const toggleAdminSimulation = () => {
  //   setSimulateAdmin(!simulateAdmin);
  //   isAdmin = !isAdmin; // Toggle admin state
  //   getMsg()?.info(
  //     !isAdmin
  //       ? "Modo administrador activado. Ahora puedes crear, editar y eliminar aseadoras."
  //       : "Modo administrador desactivado. Funciones limitadas."
  //   );
  // };

  // API calls
  const fetchAseadoras = async () => {
    try {
      setLoading(true);

      const data = await api.usersApi.getCleaners();
      if (!data) {
        // apiFetch ya muestra el toast del 403/error y retorna null.
        // Nota: el endpoint legacy /aseadoras no existe; no hacemos fallback.
        setAseadoras([]);
        return;
      }
      const mappedAseadoras: Cleaner[] = data.map((cleaner) => ({
        id: cleaner.id,
        nombre: cleaner.nombre,
        rut: cleaner.rut,
        email: cleaner.email,
        telefono: cleaner.telefono || "",
        banco: cleaner.banco || "",
        tipocuenta: cleaner.tipocuenta || "",
        numerocuenta: cleaner.numerocuenta || "",
        rol: cleaner.rol,
        sector: cleaner.sector,
        avatar: cleaner.avatar ?? null,
        avatar_url: cleaner.avatar_url ?? null,
      }));
      setAseadoras(mappedAseadoras);
    } finally {
      setLoading(false);
    }
  };

  const fetchAseos = async () => {
    setLoadingAseos(true);
    const json = await aseosApi.getAll();
    if (json) {
      const rawArray: any[] = Array.isArray(json.data) ? json.data : [];
      setAseos(rawArray.map(mapRawAseo));
      setVentanaDias(normalizarVentanaDias(json.ventanaAseosAbiertosDias));
    }
    setLoadingAseos(false);
  };

  const fetchProperties = async () => {
    setLoadingProperties(true);
    const json = await propiedadesApi.getAll();
    if (json) {
      const raw: any[] = Array.isArray(json.data) ? json.data : [];
      setProperties(
        raw.map((p) => ({
          id: p.id,
          nombre: p.nombrepropiedad ?? p.nombrePropiedad,
          tipo: p.tipo,
          habitaciones: p.habitaciones ?? 0,
          banos: p.banos ?? 0,
          costoLimpieza: p.precio_limpieza_cobrado ?? 0,
          sueldoAseador: p.precio_limpieza_aseador ?? 0,
          // Defaults por subítem para pre-rellenar el desglose del aseo
          // (cascada D-B). costoLimpieza = suma de estos 4 (invariante
          // forzado en property.model.js).
          transporteTotal: p.precio_transporte_total ?? 0,
          materialesTotal: p.precio_materiales_total ?? 0,
          lavanderiaTotal: p.precio_lavanderia_total ?? 0,
          sector: mapSectorIdToName(p.sector),
        })),
      );
    }
    setLoadingProperties(false);
  };

  const aseadoraById = useMemo(() => {
    const map = aseadoras.reduce<Record<number, string>>((acc, a) => {
      acc[a.id] = a.nombre;
      return acc;
    }, {});
    // Cuando el usuario es aseador, no llamamos /users/cleaners,
    // así que agregamos su propio nombre para que sus aseos lo muestren.
    const uid = userFromContext?.id;
    if (uid != null && !map[uid] && userFromContext?.nombre_usuario) {
      map[uid] = userFromContext.nombre_usuario;
    }
    return map;
  }, [aseadoras, userFromContext?.id, userFromContext?.nombre_usuario]);

  // Avatar del aseador por id → carita en la columna "Aseador" de /aseos.
  // Incluye al propio aseador logueado (que no aparece en /users/cleaners).
  const aseadorAvatarById = useMemo(() => {
    const map = aseadoras.reduce<
      Record<number, { avatar?: string | null; avatarUrl?: string | null }>
    >((acc, a) => {
      acc[a.id] = { avatar: a.avatar ?? null, avatarUrl: a.avatar_url ?? null };
      return acc;
    }, {});
    const uid = userFromContext?.id;
    if (uid != null && !map[uid]) {
      map[uid] = {
        avatar: userFromContext?.avatar ?? null,
        avatarUrl: userFromContext?.avatar_url ?? null,
      };
    }
    return map;
  }, [
    aseadoras,
    userFromContext?.id,
    userFromContext?.avatar,
    userFromContext?.avatar_url,
  ]);

  const propertyById = useMemo(() => {
    return properties.reduce<Record<number, Property>>((acc, p) => {
      acc[p.id] = p;
      return acc;
    }, {});
  }, [properties]);

  const aseosConNombre = useMemo(() => {
    return aseos.map((a) => {
      // lookup aseadora (la responsable — espejo legacy)
      const nombreA =
        a.idAseador != null ? (aseadoraById[a.idAseador] ?? "") : "";
      const avatarA =
        a.idAseador != null ? aseadorAvatarById[a.idAseador] : undefined;

      // Round 2026-08-25 — nombres de TODO el equipo (para display y filtro).
      // La API ya trae `aseadores` con nombre; el fallback resuelve por id
      // con el lookup local (payloads WS o respuestas crudas).
      const nombresAseadoras = (
        a.aseadores?.length
          ? a.aseadores.map((x) => x.nombre || aseadoraById[x.usuario_id] || "")
          : (a.aseadorIds ?? []).map((id) => aseadoraById[id] ?? "")
      ).filter(Boolean);

      // lookup propiedad
      const prop = a.idPropiedad != null ? propertyById[a.idPropiedad] : null;

      return {
        ...a,
        nombreAseador: nombreA,
        nombresAseadoras,
        avatarAseador: avatarA?.avatar ?? null,
        avatarUrlAseador: avatarA?.avatarUrl ?? null,
        propiedad: prop?.nombre ?? "––",
        sector: mapSectorIdToName(a?.sector),
      };
    });
  }, [aseos, aseadoraById, aseadorAvatarById, propertyById, mapSectorIdToName]);

  const userId = user?.id ?? null;

  const handleMarcarTodoPagado = async () => {
    setProcessingPago(true);
    await aseosApi.pagarTodo();
    setProcessingPago(false);
  };

  const aseosDisponibles = useMemo(() => {
    return (
      aseosConNombre
        .filter(
          (a) => a.idAseador == null && !["completado", "pagado"].includes(a.estado)
        )
        // Ventana de 14 días (round 2026-10-28): la aseadora solo ve los abiertos hasta hoy+14.
        // El servidor ya no entrega los más lejanos; esto cubre lo que llega por WebSocket y la
        // pestaña que lleva abierta días. El admin ve todos los abiertos.
        .filter((a) => isAdmin || aseoAbiertoEnVentana(a.fechaInicio, undefined, ventanaDias))
    );
  }, [aseosConNombre, isAdmin, ventanaDias]);

  const misAseos = useMemo(() => {
    // Mostramos todos los aseos pendientes/en_proceso asignados (sin recortar
    // por semana). El filtro previo a [lunes, domingo] hacía que un aseo
    // tomado para otra semana desapareciera de la tab "Aseos por hacer"
    // (también ya no estaba en "Aseos abiertos" porque idAseador != null).
    // Para acotar por fecha, el usuario tiene "Filtros avanzados".
    return aseosConNombre
      .filter((a) => ["pendiente", "en_proceso"].includes(a.estado))
      // Round 2026-08-25: "míos" = responsable O acompañante del reparto
      // (predicado compartido esAseoDeLaAseadora — review 2026-09-07).
      .filter((a) => isAdmin || esAseoDeLaAseadora(a, userId));
  }, [aseosConNombre, isAdmin, userId]);

  const aseosTerminados = useMemo(() => {
    return aseosConNombre
      .filter((a) => ["completado", "pagado"].includes(a.estado))
      .filter((a) => isAdmin || esAseoDeLaAseadora(a, userId));
  }, [aseosConNombre, isAdmin, userId]);

  const aseosAll = useMemo(
    () => [...aseosDisponibles, ...misAseos, ...aseosTerminados],
    [aseosDisponibles, misAseos, aseosTerminados],
  );

  const aseadoresConAseos = useMemo(() => {
    const s = new Set<number>();
    aseosAll.forEach((a: any) => {
      if (a?.idAseador != null) s.add(Number(a.idAseador));
      // Round 2026-08-25: las acompañantes del reparto también "tienen aseos"
      // (bloquean su eliminación igual que la responsable).
      for (const id of a?.aseadorIds ?? []) s.add(Number(id));
    });
    return s;
  }, [aseosAll]);

  // Re-fetch aseos cuando el WebSocket reconecta para sincronizar estado
  const prevConnected = useRef(false);
  useEffect(() => {
    if (isConnected && !prevConnected.current) {
      // Acabamos de reconectar — sincronizar datos
      fetchAseos();
    }
    prevConnected.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    if (!socket) {
      return;
    }

    const handler = (evt: MessageEvent) => {
      let msg: any;
      try {
        msg = JSON.parse(evt.data);
      } catch {
        console.warn("WS: mensaje no JSON:", evt.data);
        return;
      }

      // Si no tiene type o no es uno de los que manejas, salimos
      if (typeof msg.type !== "string") return;
      // Un aviso nuevo de la campana («N aseos nuevos disponibles…»): un abierto lejano ENTRÓ a la ventana
      // por el cron o porque el admin lo acercó, y eso no viaja como aseo por este canal. Sin recargar, la
      // aseadora que tiene /aseos abierto (el /home la manda acá) vería el aviso pero no el aseo (round 2026-10-28,
      // hallado por la E2E avisos-ventana).
      if (msg.type === "NOTIFICACION_NUEVA") {
        void fetchAseos();
        return;
      }
      if (
        ![
          "ASEO_ABIERTO",
          "ASEO_ASIGNADO",
          "ASEO_CANCELADO",
          "ASEO_ELIMINADO",
          "ASEO_ABIERTO_ACTUALIZADO",
          "ASEO_ASIGNADO_ACTUALIZADO",
          "ASEO_PAGADO",
        ].includes(msg.type)
      ) {
        return;
      }

      // Ahora sí esperamos msg.data.aseo
      if (!msg.data || typeof msg.data !== "object") {
        console.warn("WS: payload data inválido:", msg.data);
        return;
      }
      const raw: AseoRaw = msg.data as AseoRaw;
      const aseo = mapRawAseo(raw);

      switch (msg.type) {
        case "ASEO_ABIERTO":
          // Admin ve todo
          if (isAdmin) {
            setAseos((prev) => [aseo, ...prev]);
          }
          // Aseador ve solo si coincide sector
          if (!isAdmin && user?.sector.includes(aseo.sector)) {
            setAseos((prev) => [aseo, ...prev]);
          }
          break;

        case "ASEO_ASIGNADO":
          // Admin ve todo (estado actualizado)
          if (isAdmin) {
            setAseos((prev) =>
              prev.some((x) => x.id === aseo.id)
                ? prev.map((x) => (x.id === aseo.id ? aseo : x))
                : [aseo, ...prev],
            );
          }
          if (!isAdmin) {
            // Round 2026-08-25: "mío" = responsable O acompañante del reparto
            // (el payload WS trae `aseador_ids` con el equipo completo).
            if (esAseoDeLaAseadora(aseo, user?.id)) {
              // Es mío: actualizar o agregar
              setAseos((prev) =>
                prev.some((x) => x.id === aseo.id)
                  ? prev.map((x) => (x.id === aseo.id ? aseo : x))
                  : [...prev, aseo],
              );
            } else {
              // Otro aseador lo tomó: remover de disponibles
              setAseos((prev) => prev.filter((x) => x.id !== aseo.id));
            }
          }
          break;
        case "ASEO_CANCELADO":
          // Admin ve todo (estado actualizado)
          if (isAdmin) {
            setAseos((prev) =>
              prev.some((x) => x.id === aseo.id)
                ? prev.map((x) => (x.id === aseo.id ? aseo : x))
                : [aseo, ...prev],
            );
          }
          // Aseador: quitar de misAseos y re-agregar a disponibles si es de su sector
          if (!isAdmin) {
            setAseos((prev) => {
              const without = prev.filter((x) => x.id !== aseo.id);
              if (user?.sector.includes(aseo.sector)) {
                return [aseo, ...without];
              }
              return without;
            });
          }
          break;
        case "ASEO_ELIMINADO":
          setAseos((prev) => prev.filter((a) => a.id !== aseo.id));
          break;

        // ---------------------------------------------------
        // Un aseo abierto fue modificado (p. ej. cambiaron fecha, observaciones…)
        case "ASEO_ABIERTO_ACTUALIZADO":
          // Admin ve todas las actualizaciones
          if (isAdmin) {
            setAseos((prev) =>
              prev.some((a) => a.id === aseo.id)
                ? prev.map((a) => (a.id === aseo.id ? aseo : a))
                : [aseo, ...prev],
            );
          }
          // Aseador ve la actualización sólo si sigue en su sector
          else if (user?.sector.includes(aseo.sector)) {
            setAseos((prev) =>
              prev.some((a) => a.id === aseo.id)
                ? prev.map((a) => (a.id === aseo.id ? aseo : a))
                : [aseo, ...prev],
            );
          }
          break;

        // ---------------------------------------------------
        // Un aseo asignado fue modificado (p. ej. se cambió el aseador, el estado, etc.)
        case "ASEO_ASIGNADO_ACTUALIZADO":
          if (isAdmin) {
            // Admin ve todas las actualizaciones
            setAseos((prev) =>
              prev.some((a) => a.id === aseo.id)
                ? prev.map((a) => (a.id === aseo.id ? aseo : a))
                : [aseo, ...prev],
            );
          } else {
            // Si me sigue asignado (responsable o acompañante), actualizo o agrego
            if (esAseoDeLaAseadora(aseo, user?.id)) {
              setAseos((prev) =>
                prev.some((a) => a.id === aseo.id)
                  ? prev.map((a) => (a.id === aseo.id ? aseo : a))
                  : [aseo, ...prev],
              );
            }
            // Si ya no me asignaron, lo elimino de mi lista
            else {
              setAseos((prev) => prev.filter((a) => a.id !== aseo.id));
            }
          }
          break;
        case "ASEO_PAGADO":
          setAseos((prev) =>
            prev.map((a) =>
              a.id === aseo.id ? { ...a, estado: "pagado" } : a,
            ),
          );
          break;
        default:
          console.warn("WS desconocido:", msg);
      }
    };

    socket.addEventListener("message", handler);
    return () => {
      socket.removeEventListener("message", handler);
    };
  }, [socket, user, isAdmin]);

  const showModal = (record?: Cleaner) => {
    if (!isAdmin) {
      getMsg()?.error("Solo los administradores pueden gestionar aseadoras");
      return;
    }

    if (record) {
      setEditingAseadora(record);
      form.setFieldsValue({
        ...record,
        password: "",
      });
      getMsg()?.info(`Editando información de ${record.nombre}`);
    } else {
      setEditingAseadora(null);
      form.resetFields();
      form.setFieldsValue({
        rol: "aseador",
      });
    }
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  // El rechazo del servidor por el teléfono (409 duplicado, 400 formato) se marca además junto al campo.
  const alFallarTelefonoAseadora = (error: ErrorApi) => {
    marcarCampoTelefono(form, "telefono", error);
  };

  // Campo del formulario → campo de la API, para mandar solo lo que cambió.
  const CAMPO_API_ASEADORA = {
    nombre: "nombre_usuario",
    email: "email_usuario",
    rut: "rut_quien_recibe",
    banco: "banco_quien_recibe",
    tipocuenta: "tipocuenta_quien_recibe",
    numerocuenta: "numerocuenta_quien_recibe",
    telefono: "telefono_usuario",
    sector: "sector",
  } as const;

  const handleSubmit = async () => {
    // El candado se toma ANTES de validar: `validateFields` es asíncrono y dos clics seguidos entrarían los dos.
    if (guardandoAseadoraRef.current) return;
    guardandoAseadoraRef.current = true;
    try {
      let values: any;
      try {
        values = await form.validateFields();
      } catch (errorValidacion) {
        // Los errores de cada campo ya quedaron marcados en el formulario; falta llevar a la persona hasta el primero (el modal es
        // más alto que una pantalla de celular y, sin esto, «Actualizar» no hacía NADA visible).
        irAlPrimerError(form, errorValidacion);
        return;
      }
      setSavingAseadora(true);

      // Always set role to aseador
      values.rol = ["aseador"];

      if (editingAseadora) {
        // Edición: solo viaja lo que la persona CAMBIÓ respecto de la fila que vio al abrir el modal. Antes se reenviaban todos los
        // campos: si otra persona cambiaba el teléfono mientras este modal estaba abierto, un cambio de nombre lo pisaba con el
        // viejo; y obligaba a tener completos RUT y datos bancarios solo para corregir un teléfono.
        // OJO: el form incluye `sector` (multi-select); si cambia, tiene que viajar (bug histórico hasta 2026-05).
        // El campo "Nueva contraseña" del modal: vacío = mantener la actual. Solo lo enviamos si el admin escribió algo. Hasta
        // junio 2026 este campo no se mandaba (bug): el admin creía resetear la clave pero el aseador no podía entrar con la
        // nueva. Ver updateCleaner (backend).
        const nuevaPassword =
          typeof values.password === "string" ? values.password.trim() : "";

        const cambiados = clavesCambiadas(
          editingAseadora as unknown as Record<string, unknown>,
          values,
          Object.keys(CAMPO_API_ASEADORA) as (keyof typeof CAMPO_API_ASEADORA)[],
        );
        if (cambiados.length === 0 && !nuevaPassword) {
          getMsg()?.info("No hay cambios que guardar.");
          setModalVisible(false);
          form.resetFields();
          return;
        }

        const updateData: Record<string, unknown> = {};
        for (const k of cambiados) {
          // Vaciar el teléfono se manda como null (explícito); nunca se borra por un valor que no se entiende.
          updateData[CAMPO_API_ASEADORA[k]] = k === "telefono" ? telefonoParaGuardar(values[k]) : values[k];
        }
        if (nuevaPassword) updateData.password = nuevaPassword;

        const result = await api.usersApi.updateUser(
          editingAseadora.id,
          updateData as UpdateUserRequest,
          { alFallar: alFallarTelefonoAseadora },
        );

        // `updateUser` (apiFetch) ya mostró el motivo REAL que dio el servidor, p. ej. el 409 TELEFONO_DUPLICADO:
        // «Ese teléfono ya lo usa <nombre> (<rol>). Cada administrador o aseador debe tener su propio número.»
        // Lanzar acá un error genérico mostraba un segundo toast («Error al guardar la aseadora») encima del
        // que explica qué pasó y, como la app muestra como máximo 3 avisos a la vez, podía sacarlo de la pantalla.
        // El modal queda abierto para que se corrija el dato, y el campo del teléfono queda marcado.
        if (!result) return;

        // Confirmación explícita al admin cuando reseteó la contraseña, para
        // que sepa que la nueva ya quedó activa (y las sesiones viejas caen).
        if (nuevaPassword) {
          getMsg()?.success(
            "Contraseña del aseador actualizada. Debe iniciar sesión con la nueva.",
          );
        }
      } else {
        // For new aseadora, use the auth/register endpoint with telefono field included
        const registerData = {
          ...values,
          telefono: telefonoParaGuardar(values.telefono) ?? "", // Ensure telefono is included in the register request
        };

        const result = await api.authApi.register(registerData, { alFallar: alFallarTelefonoAseadora });

        // Igual que arriba: `register` ya mostró el motivo del servidor (teléfono repetido, correo ya usado…).
        if (!result) return;
      }

      setModalVisible(false);
      form.resetFields();
      fetchAseadoras();
    } catch (error) {
      console.error("Error saving aseadora:", error);
      getMsg()?.error("Error al guardar la aseadora");
    } finally {
      guardandoAseadoraRef.current = false;
      setSavingAseadora(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin) {
      getMsg()?.error("Solo los administradores pueden eliminar aseadoras");
      return;
    }

    try {
      setLoading(true);
      const aseadoraToDelete = aseadoras.find((a) => a.id === id);

      // Use the new deleteUser API endpoint
      const result = await api.usersApi.deleteUser(id);

      if (!result) {
        throw new Error("Error al eliminar la aseadora");
      }

      getMsg()?.success(
        `Aseadora ${aseadoraToDelete?.nombre || ""} eliminada correctamente`,
      );
      setAseadoras((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error("Error deleting aseadora:", error);
      getMsg()?.error("Error al eliminar la aseadora.");
    } finally {
      setLoading(false);
    }
  };

  const showAseoModal = (record?: Aseo) => {
    if (!isAdmin) {
      getMsg()?.error("Solo los administradores pueden gestionar aseos");
      return;
    }

    // Remonta el editor de notas embebido en cada apertura (ver aseoModalNonce).
    setAseoModalNonce((n) => n + 1);

    if (record) {
      setEditingAseo(record);
      // Desglose de los 4 subítems para pre-rellenar el modal. La FUENTE es
      // `subitemsResolved` (lo calcula el backend: para aseos vinculados manda
      // el override del arriendo, NO la copia del aseo). Si falta (respuesta
      // legacy), caemos a los planos costoLimpieza/sueldoAseador del record.
      // El `sueldoAseador` ES el subítem "pago al aseador"; `costoLimpieza` es
      // la suma de los 4 (campo disabled en el modal). Round 2026-06-19 (D-B).
      const sr = record.subitemsResolved;
      const subAseador = sr ? sr.aseador : (record.sueldoAseador ?? 0);
      const subTransporte = sr ? sr.transporte_total : 0;
      const subMateriales = sr ? sr.materiales_total : 0;
      const subLavanderia = sr ? sr.lavanderia_total : 0;
      // Round 2026-08-25 — equipo + reparto para el editor multi-aseadora.
      // `aseadores` trae los montos para admin; si faltan (dato legacy),
      // partes iguales del sueldo actual como punto de partida.
      const equipo = record.aseadores?.length
        ? record.aseadores
        : record.idAseador != null
          ? [{ usuario_id: record.idAseador, monto: record.sueldoAseador ?? 0 }]
          : [];
      const montosCompletos = equipo.every((x) => typeof x.monto === "number");
      // Mismo algoritmo que el componente y el backend — helper compartido,
      // nunca copiado (review 2026-09-07: acá había una tercera copia inline
      // de la regla de reparto, la clase exacta que CLAUDE.md §7 prohíbe).
      const partesIguales = repartirPartesIgualesUI(subAseador, equipo.length || 1);
      aseoForm.setFieldsValue({
        ...record,
        idAseadores: equipo.map((x) => x.usuario_id),
        asignaciones: equipo.map((x, i) => ({
          usuario_id: x.usuario_id,
          monto: montosCompletos ? (x.monto as number) : partesIguales[i],
        })),
        sueldoAseador: subAseador,
        subTransporte,
        subMateriales,
        subLavanderia,
        costoLimpieza: subAseador + subTransporte + subMateriales + subLavanderia,
        fechaInicio: record.fechaInicio
          ? dayjs(record.fechaInicio, "YYYY-MM-DD")
          : null,
        fechaFin: record.fechaFin ? dayjs(record.fechaFin, "YYYY-MM-DD") : null,
        horaInicio: record.horaInicio
          ? dayjs(record.horaInicio, "HH:mm")
          : null,
      });
      setAseoAssignmentMode(record.modoAsignacion || "designado");

      const property = properties.find((p) => p.id === record.idPropiedad);
      setSelectedProperty(property || null);
    } else {
      setEditingAseo(null);
      aseoForm.resetFields();
      setSelectedProperty(null);
      setAseoAssignmentMode("abierto");

      aseoForm.setFieldsValue({
        estado: "pendiente",
        modoAsignacion: "abierto",
        horaInicio: dayjs("11:00", "HH:mm"),
        fechaInicio: ahoraChile(),
        fechaFin: ahoraChile(),
      });
    }
    setAseoModalVisible(true);
  };

  const handleAseoCancel = () => {
    setAseoModalVisible(false);
    aseoForm.resetFields();
    // Las notas del modal de edición se guardan al instante por su propio
    // endpoint (sin broadcast WS): aunque el usuario "cancele" el modal, el
    // texto de notas puede haber cambiado — refetch para que la tabla y el
    // "Buscar en notas" no queden stale (auditoría 2026-07-13).
    void fetchAseos();
  };

  const handleAseoSubmit = async () => {
    if (!isAdmin) {
      getMsg()?.error("Solo los administradores pueden crear o modificar aseos");
      return;
    }
    // Guard de re-entrada: dos clics rápidos en "Crear" no deben crear 2 aseos
    // ni re-subir los archivos staged. El ref corta la carrera sincrónicamente.
    if (submittingAseoRef.current) return;
    submittingAseoRef.current = true;
    setSubmittingAseo(true);

    try {
      const values = await aseoForm.validateFields();

      // Round 2026-06-19 (cascada D-B): el desglose de 4 subítems es la FUENTE.
      // El "pago al aseador" (`sueldoAseador`) es el subítem `aseador`; el
      // costo total es la suma de los 4 (NO confiamos en el campo disabled,
      // lo recalculamos acá). Se manda `subitemsOverride` al backend, que lo
      // propaga al arriendo si el aseo está vinculado (mapAseoData +
      // recalcBookingFinancialsFromSubitems).
      const subAseador = Number(values.sueldoAseador) || 0;
      const subTransporte = Number(values.subTransporte) || 0;
      const subMateriales = Number(values.subMateriales) || 0;
      const subLavanderia = Number(values.subLavanderia) || 0;
      const costoLimpieza =
        subAseador + subTransporte + subMateriales + subLavanderia;
      const sueldoAseador = subAseador;

      const subitemsOverride = {
        aseador: subAseador,
        transporte_total: subTransporte,
        materiales_total: subMateriales,
        lavanderia_total: subLavanderia,
      };

      if (values.modoAsignacion === "abierto") {
        values.idAseador = null;
        values.nombreAseador = null;
        values.asignacionActiva = false;
        values.aseadorasRechazados = [];
        // Sin equipo: el backend limpia `asignaciones` al ver idAseador null.
        delete values.idAseadores;
        delete values.asignaciones;
      } else if (!values.asignaciones?.length && !values.idAseador) {
        getMsg()?.error("Debe seleccionar al menos una aseadora en modo designado");
        return;
      } else {
        values.asignacionActiva = true;
        // Round 2026-08-25 — el reparto viaja como `asignaciones` (el
        // componente RepartoAseadorasField ya validó que la suma calce con
        // el sueldo; el backend re-valida igual). La responsable va además
        // en idAseador/nombreAseador (espejos legacy). Si el admin cambió el
        // sueldo y apretó Guardar antes de que el watch re-escalara, acá se
        // re-escala proporcional una última vez para que nunca viaje un
        // reparto que no suma el total (el backend lo rechazaría con 400).
        if (values.asignaciones?.length) {
          const sumaReparto = values.asignaciones.reduce(
            (acc: number, a: any) => acc + (Number(a.monto) || 0),
            0,
          );
          if (sumaReparto !== sueldoAseador) {
            values.asignaciones = reescalarProporcionalUI(
              values.asignaciones,
              sueldoAseador,
            );
          }
          values.idAseador = values.asignaciones[0].usuario_id;
        }
        delete values.idAseadores;
      }

      // Round 2026-05-13 — el aseo ya no lleva `pagado_a` ni
      // `rol_de_aloja_facil`. Solo validamos que `pagado_por.tipo` sea uno
      // de los 3 valores soportados (0=AF, 1=propietario, 2=arrendatario).
      const ppTipo = values.pagado_por?.tipo;
      if (ppTipo == null || ![0, 1, 2].includes(Number(ppTipo))) {
        getMsg()?.error(
          "Selecciona quién paga el aseo (Aloja Fácil, Propietario o Arrendatario).",
        );
        return;
      }

      // Quitamos los campos auxiliares del form que el backend no espera como
      // planos (van dentro de subitemsOverride).
      const {
        subTransporte: _t,
        subMateriales: _m,
        subLavanderia: _l,
        ...valuesLimpios
      } = values;

      const payload = {
        ...valuesLimpios,
        costoLimpieza,
        sueldoAseador,
        subitemsOverride,
        fechaInicio: values.fechaInicio
          ? dayjs(values.fechaInicio).format("YYYY-MM-DD")
          : null,
        fechaFin: values.fechaFin
          ? dayjs(values.fechaFin).format("YYYY-MM-DD")
          : null,
        horaInicio: values.horaInicio
          ? dayjs(values.horaInicio).format("HH:mm")
          : null,
      };

      // Advertencia «esta aseadora ya tiene otro aseo ese día» (round
      // 2026-09-11): pide confirmación si cambió el equipo o las fechas y hay
      // cruces — no bloquea. `original` = lo que estaba guardado.
      // Ver components/AvisoAseosMismoDia.tsx.
      if (values.modoAsignacion !== "abierto") {
        const consulta = consultaMismoDia({
          usuarioIds: values.asignaciones?.length
            ? values.asignaciones.map((a: any) => a?.usuario_id)
            : [values.idAseador],
          desde: payload.fechaInicio,
          hasta: payload.fechaFin,
          excluirAseoId: editingAseo?.id ?? null,
        });
        const original = editingAseo
          ? consultaMismoDia({
              usuarioIds: editingAseo.aseadorIds ?? [],
              desde: editingAseo.fechaInicio,
              hasta: editingAseo.fechaFin,
              excluirAseoId: editingAseo.id,
            })
          : null;
        if (!(await confirmarAseosMismoDia(consulta, original))) return;
      }

      if (editingAseo) {
        const json = await aseosApi.update(editingAseo.id, payload);
        // Si falla (apiFetch retorna null, no lanza), NO cerramos el modal:
        // así el admin puede reintentar sin re-tipear.
        if (!json?.data) return;
        // Warning del backend (ej. mes cerrado: el aseo se guardó pero el
        // cambio del desglose NO se propagó a la liquidación del cliente).
        // Mismo patrón que el calendario — round 2026-08-02.
        const warning = (json as any).warning;
        if (warning?.message) {
          getMsg()?.warning({ content: warning.message, duration: 10 });
        }
        setAseos((prev) =>
          prev.map((aseo) =>
            aseo.id === editingAseo.id ? mapRawAseo(json.data) : aseo,
          ),
        );
      } else {
        const json = await aseosApi.create(payload);
        // Si falla, NO cerramos el modal: preserva el borrador de notas (texto
        // + archivos staged) para reintentar. Cerrar acá los perdería.
        if (!json?.data) return;
        const nuevoId = json.data.id;
        // Persistir las notas en BORRADOR (texto + fotos/videos/documentos)
        // que el admin cargó en el modal de creación. El aseo ya existe
        // (recién creado), así que ahora sí hay id para subir los archivos.
        if (nuevoId != null && notesDraftRef?.current?.hasContent()) {
          try {
            await notesDraftRef.current.commit(nuevoId);
          } catch (e) {
            console.error("Error guardando las notas del aseo nuevo:", e);
            getMsg()?.warning(
              "El aseo se creó, pero hubo un problema guardando alguna nota o archivo. Ábrelo para revisarlo.",
            );
          }
          // Refrescar para que la lista (y el buscador de notas) reflejen el
          // texto recién guardado, que no viene en la respuesta de create.
          await fetchAseos();
        } else {
          setAseos((prev) => [...prev, mapRawAseo(json.data)]);
        }
      }

      // Solo se llega acá si create/update tuvo éxito (los fallos hacen return).
      setAseoModalVisible(false);
      aseoForm.resetFields();
    } catch (error) {
      console.error("Error saving aseo:", error);
      getMsg()?.error("Error al guardar el aseo");
    } finally {
      submittingAseoRef.current = false;
      setSubmittingAseo(false);
    }
  };

  const handleDeleteAseo = async (id: number) => {
    if (!isAdmin) {
      getMsg()?.error("Solo los administradores pueden eliminar aseos");
      return;
    }

    setLoadingAseos(true);
    const ok = await aseosApi.delete(id);
    if (ok !== null) {
      setAseos((prev) => prev.filter((a) => a.id !== id));
    }
    setLoadingAseos(false);
  };

  // Property actions
  const openPropertySelector = () => {
    setPropertyModalVisible(true);
  };

  const handleSelectProperty = (property: Property) => {
    setSelectedProperty(property);
    // Pre-rellenamos el desglose de 4 subítems con los defaults de la
    // propiedad (cascada D-B). costoLimpieza = suma (campo disabled). El
    // admin puede ajustar cada subítem antes de crear el aseo.
    const subAseador = property.sueldoAseador || 0;
    const subTransporte = property.transporteTotal || 0;
    const subMateriales = property.materialesTotal || 0;
    const subLavanderia = property.lavanderiaTotal || 0;
    aseoForm.setFieldsValue({
      idPropiedad: property.id,
      propiedad: property.nombre,
      sueldoAseador: subAseador || undefined,
      subTransporte,
      subMateriales,
      subLavanderia,
      costoLimpieza:
        subAseador + subTransporte + subMateriales + subLavanderia ||
        property.costoLimpieza ||
        undefined,
    });
    setPropertyModalVisible(false);
    // Reset del buscador del modal: si queda texto, la próxima apertura
    // muestra la lista filtrada sin que se note por qué (y antes además
    // contaminaba las options del filtro "Propiedad" de la barra).
    setPropertySearchText("");

    getMsg()?.success(
      `Propiedad "${
        formatPropertyName(property.nombre)
      }" seleccionada. Costo: $${property.costoLimpieza?.toLocaleString(
        "es-CL",
      )} · Sueldo aseador: $${property.sueldoAseador?.toLocaleString(
        "es-CL",
      )}`,
    );
  };

  const fetchSectors = async () => {
    setLoading(true);
    const j = await propiedadesApi.getSectores();
    const raw = j?.data ?? [];
    setSectors(raw.map((p) => ({ id: p.id, sector: p.sector })));
    setLoading(false);
  };

  const getFilteredProperties = () => {
    if (!propertySearchText) return properties;
    return properties.filter((property) =>
      fuzzyIncludes(property.nombre, propertySearchText),
    );
  };

  // Aseo management functions
  const tomarAseo = async (
    aseoId: number,
    aseadoraId: number,
    aseadoraNombre: string,
  ) => {
    const aseoToUpdate = aseos.find((a) => a.id === aseoId);
    if (!aseoToUpdate || aseoToUpdate.modoAsignacion !== "abierto") {
      getMsg()?.error("Este aseo no está disponible para tomar");
      return;
    }

    if (aseoToUpdate.aseadorasRechazados?.includes(aseadoraId)) {
      getMsg()?.error("Ya has rechazado este aseo anteriormente");
      return;
    }

    const updatedAseo: Aseo = {
      ...aseoToUpdate,
      idAseador: aseadoraId,
      nombreAseador: aseadoraNombre,
      // Tomar = ella sola por el total (round 2026-08-25).
      aseadores: [{ usuario_id: aseadoraId, nombre: aseadoraNombre }],
      aseadorIds: [aseadoraId],
      asignacionActiva: true,
    };

    try {
      // Se espera la respuesta (round 2026-10-28): antes era fire-and-forget y el aseo pasaba
      // a «Por hacer» con un «Has tomado este aseo» aunque el servidor lo hubiera negado
      // (otra aseadora lo tomó antes, o el aseo todavía no entra en la ventana de 14 días).
      // Si falla, apiFetch ya mostró el mensaje claro del servidor («Este aseo estará
      // disponible para tomar desde el …») y acá se recarga la lista para no mostrar un
      // estado que no existe.
      const res = await aseosApi.tomar(aseoId, aseadoraId);
      if (!res) {
        await fetchAseos();
        return;
      }

      setAseos((prev) => prev.map((a) => (a.id === aseoId ? updatedAseo : a)));
      getMsg()?.success("Has tomado este aseo correctamente");
    } catch (error) {
      console.error("Error al tomar el aseo:", error);
      getMsg()?.error("Hubo un problema al tomar el aseo");
    }
  };

  return {
    // States
    aseadoras,
    aseos: aseosConNombre,
    loading,
    loadingAseos,
    loadingProperties,
    modalVisible,
    savingAseadora,
    aseoModalVisible,
    aseoModalNonce,
    submittingAseo,
    propertyModalVisible,
    editingAseadora,
    editingAseo,
    sidebarOpen,
    simulateAdmin,
    aseosDisponibles, // para aseador
    aseosTerminados, // para aseador
    misAseos,
    aseadoresConAseos,
    isAdmin,
    user,
    form,
    aseoForm,
    properties,
    selectedProperty,
    propertySearchText,
    aseadorasSearchText,
    aseosSearchText,
    aseoAssignmentMode,
    filteredAseadoras,
    filteredAseos: aseosConNombre,

    // Constants
    sectors,
    availableBanks,
    setAvailableBanks,
    accountTypes,
    setAccountTypes,

    // Methods
    toggleSidebar,
    //toggleAdminSimulation,
    showModal,
    handleCancel,
    handleSubmit,
    handleDelete,
    showAseoModal,
    handleAseoCancel,
    handleAseoSubmit,
    handleDeleteAseo,
    handleMarcarTodoPagado,
    setAseoAssignmentMode,
    openPropertySelector,
    handleSelectProperty,
    getFilteredProperties,
    setPropertySearchText,
    setAseadorasSearchText,
    setAseosSearchText,
    setPropertyModalVisible, // Add this missing function
    tomarAseo,
    isConnected,
    lavanderias,
    // Round 2026-05-12 iter 39: exponemos fetchAseos para que el
    // componente lo llame tras un bulk action y refresque la tabla.
    fetchAseos,
    ventanaDias,
  };
};
